/**
 * Tenant Context Provider
 * 
 * Central tenant context provider for multi-tenant architecture. Manages current tenant state,
 * handles tenant resolution (subdomain or profile-based), applies tenant branding dynamically,
 * supports global_admin tenant switching with TanStack Query cache invalidation, and exposes
 * tenant context throughout the application via useTenantContext hook.
 * 
 * Implements defensive loading patterns with error boundaries consistent with RoleViewContext
 * and VerticalContext patterns.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { type Tenant } from '@/lib/tenantDetection';
import { applyTenantColors, applyTenantThemeMode } from '@/lib/tenantBranding';
import { useActualUserRole } from '@/hooks/useActualUserRole';
import { normalizeVerticalsToUI } from '@/lib/verticals';

/**
 * Tenant Context Type
 * 
 * Defines the shape of the tenant context value provided to all consuming components.
 * Following the pattern established in RoleViewContext for consistency.
 */
interface TenantContextType {
 tenant: Tenant | null;
 isLoading: boolean;
 isGlobalAdmin: boolean;
 error: string | null;
 switchTenant: (tenantId: string | null) => Promise<void>;
 refreshTenant: (options?: { skipPersist?: boolean }) => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * Tenant Provider Component
 * 
 * Wraps the application and provides tenant context to all child components.
 * Loads tenant on mount, checks global admin status, and applies tenant branding.
 * Follows the defensive programming pattern from RoleViewContext.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
 const { user, loading: authLoading } = useSupabaseAuth();
 const { data: userRoleData, isLoading: roleLoading } = useActualUserRole(user?.id);
 const [tenant, setTenant] = useState<Tenant | null>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 
 // Use authoritative isGlobalAdmin from useActualUserRole
 const isGlobalAdmin = userRoleData?.isGlobalAdmin ?? false;

 /**
 * Load tenant for the current user
 * 
 * This function:
 * 1. Checks if user is a global_admin via profile_with_role view
 * 2. Resolves tenant using tenantDetection utility (subdomain or profile-based)
 * 3. Applies tenant branding via applyTenantColors
 * 4. Handles errors gracefully with fallback states
 */
 const loadTenantForUser = useCallback(async (
 userId: string | null,
 options?: { skipPersist?: boolean },
 ) => {
 setError(null);
 setIsLoading(true);
 const skipPersist = options?.skipPersist ?? false;

 if (!userId) {
 // User not authenticated - reset all state
 setTenant(null);
 setIsLoading(false);
 
 // Apply default branding (null tenant = default BLUE_THEME)
 applyTenantColors(null);
 return;
 }

 try {
 // isGlobalAdmin is now derived from useActualUserRole hook
 // No need to query profile_with_role here

 // Resolve tenant using tenantDetection utility
 // This handles both subdomain-based and profile-based tenant resolution
 // depending on VITE_FEATURES_MULTI_TENANCY feature flag
 const { resolveTenant } = await import('@/lib/tenantDetection');
 const resolvedTenant = await resolveTenant(supabase, userId);

 if (resolvedTenant) {
 const normalizedTenant: Tenant = {
 ...resolvedTenant,
 enabled_verticals: normalizeVerticalsToUI(resolvedTenant.enabled_verticals),
 };

 // IMPORTANT:
 // `loadTenantForUser` is used by both initial tenant resolution AND post-switch refreshes.
 // For post-switch refreshes, we MUST NOT re-persist the resolved tenant back to the DB,
 // otherwise we can accidentally undo a tenant switch if resolution races or reads stale state.
 if (!skipPersist) {
 // Persist current tenant selection in DB so RLS + cache keys align with UI selection.
 // This is safe for normal users (must be a member). For global_admins, backend will allow setting context.
 try {
 await supabase.rpc("update_last_accessed_tenant", { p_tenant_id: normalizedTenant.id });
 } catch (rpcErr) {
 // Non-fatal: tenant resolution can still work even if we can't record last access.
 console.warn("⚠️ [TenantContext] Failed to update_last_accessed_tenant:", rpcErr);
 }
 }

 setTenant(normalizedTenant);
 setError(null);

 // Apply tenant branding dynamically
 // applyTenantColors updates CSS custom properties that Tailwind references
 applyTenantColors(normalizedTenant);
 applyTenantThemeMode(normalizedTenant);

 console.log('✅ [TenantContext] Tenant loaded:', normalizedTenant.name);
 } else {
 // No tenant found - this should not happen after migration
 setTenant(null);
 setError('Could not resolve tenant for user');
 console.warn('⚠️ [TenantContext] No tenant found for user');
 
 // Apply default branding as fallback
 applyTenantColors(null);
 }
 } catch (err) {
 const errorMessage = err instanceof Error ? err.message : 'Failed to load tenant';
 setError(errorMessage);
 console.error('❌ [TenantContext] Error loading tenant:', err);
 setTenant(null);
 
 // Apply default branding on error
 applyTenantColors(null);
 } finally {
 setIsLoading(false);
 }
 }, []);

 /**
 * Effect to load tenant when authentication state changes
 * Waits for auth to complete before loading tenant data
 */
 useEffect(() => {
 if (authLoading) return;
 loadTenantForUser(user?.id ?? null);
 }, [authLoading, user?.id, loadTenantForUser]);

 /**
 * Live branding updates
 *
 * Subscribe to the current tenant row so branding changes apply instantly
 * (no manual refresh required) for the active session.
 */
 useEffect(() => {
 if (!tenant?.id) return;

 const channel = supabase
 .channel(`tenant-branding-${tenant.id}`)
 .on(
 'postgres_changes',
 { event: 'UPDATE', schema: 'public', table: 'tenants', filter: `id=eq.${tenant.id}` },
 (payload) => {
 const updated = payload.new as Tenant;
 const normalizedUpdated: Tenant = {
 ...updated,
 enabled_verticals: normalizeVerticalsToUI(updated.enabled_verticals),
 };
 setTenant(normalizedUpdated);
 applyTenantColors(normalizedUpdated);
 applyTenantThemeMode(normalizedUpdated);
 }
 )
 .subscribe();

 return () => {
 supabase.removeChannel(channel);
 };
 }, [tenant?.id]);

 /**
 * Refresh tenant data
 * 
 * Reloads the current tenant from the database.
 * Useful after tenant configuration changes.
 */
 const refreshTenant = useCallback(async (options?: { skipPersist?: boolean }) => {
 // Default to skipping persistence during refreshes to avoid undoing recent switches.
 // Callers that explicitly want persistence can pass { skipPersist: false }.
 const skipPersist = options?.skipPersist ?? true;
 await loadTenantForUser(user?.id ?? null, { skipPersist });
 }, [loadTenantForUser, user?.id]);

 /**
 * Switch tenant (global_admin only)
 * 
 * Allows global_admins to switch between tenants for platform management.
 * Uses localStorage override mechanism for development and testing.
 * 
 * Note: TanStack Query cache invalidation is handled by useTenantSwitcher hook
 * that consumers should use alongside this context.
 * 
 * @param tenantId - Target tenant ID, or null to clear override
 */
 const switchTenant = useCallback(async (tenantId: string | null) => {
 if (!user?.id) {
 console.warn('⚠️ [TenantContext] Cannot switch tenant: no user logged in');
 return;
 }

 if (!isGlobalAdmin) {
 console.warn('⚠️ [TenantContext] Cannot switch tenant: user is not global admin');
 return;
 }

 try {
 // Set override in localStorage using tenantDetection utility
 const { setTenantOverride } = await import('@/lib/tenantDetection');
 setTenantOverride(user.id, tenantId);

 // Persist switch in DB as well (keeps get_current_user_tenant_id() aligned)
 if (tenantId) {
 await supabase.rpc("update_last_accessed_tenant", { p_tenant_id: tenantId });
 }

 // Reload tenant with new override
 await refreshTenant({ skipPersist: true });

 console.log('✅ [TenantContext] Switched tenant:', tenantId);
 } catch (err) {
 console.error('❌ [TenantContext] Failed to switch tenant:', err);
 setError(err instanceof Error ? err.message : 'Failed to switch tenant');
 }
 }, [user?.id, isGlobalAdmin, refreshTenant]);

 /**
 * Memoized context value
 * Prevents unnecessary re-renders of consuming components
 */
 const contextValue = useMemo(() => ({
 tenant,
 isLoading: isLoading || roleLoading, // Wait for BOTH tenant and role to load
 isGlobalAdmin,
 error,
 switchTenant,
 refreshTenant,
 }), [tenant, isLoading, roleLoading, isGlobalAdmin, error, switchTenant, refreshTenant]);

 return (
 <TenantContext.Provider value={contextValue}>
 {children}
 </TenantContext.Provider>
 );
}

/**
 * useTenantContext Hook
 * 
 * Access tenant context from any component within TenantProvider.
 * Implements defensive error handling following RoleViewContext pattern.
 * 
 * Throws helpful error messages if called outside provider, but returns
 * safe fallback values to prevent application crashes.
 * 
 * @returns TenantContextType with tenant data, loading states, and management functions
 */
export const useTenantContext = () => {
 const context = useContext(TenantContext);

 if (context === undefined) {
 console.error('❌ [TenantContext] useTenantContext called outside TenantProvider');
 console.error('Current location:', window.location.pathname);
 console.error('Call stack:', new Error().stack);

 // Return safe fallback values to prevent application crashes
 // This defensive pattern allows the app to continue functioning even if
 // the context is accessed incorrectly, while logging the error for debugging
 return {
 tenant: null,
 isLoading: true,
 isGlobalAdmin: false,
 error: 'Context not available',
 switchTenant: async () => {},
 refreshTenant: async () => {},
 } as TenantContextType;
 }

 return context;
};
